import axios, { AxiosError } from "axios";
import type { ApiResponse } from "../types/shared";

const httpClient = axios.create({
  baseURL: import.meta.env.VITE_BASE_API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const handleErrorResponse = (error: AxiosError) => {
  if (axios.isAxiosError(error)) {
    const { response } = error;

    // Try to get message from response.data (API response structure)
    let message: string | undefined;

    if (response?.data) {
      // Check if it's the ApiResponse structure
      if (typeof response.data === "object" && response.data !== null) {
        const apiResponse = response.data as Partial<ApiResponse<any>>;
        // ApiResponse has message at the top level
        if (apiResponse.message) {
          message = apiResponse.message;
        }
        // Also check for nested message in data.data.message (some APIs nest it)
        else if ((apiResponse as any)?.data?.message) {
          message = (apiResponse as any).data.message;
        }
      }
    }

    // Fallback: try to parse from request response if available
    if (!message && error.request?.response) {
      try {
        const parsedResponse =
          typeof error.request.response === "string"
            ? JSON.parse(error.request.response)
            : error.request.response;
        if (parsedResponse?.message) {
          message = parsedResponse.message;
        }
      } catch (e) {
        // If parsing fails, ignore
      }
    }

    switch (response?.status) {
      case 400: {
        return message || "Invalid request body or connection issues.";
      }
      case 401: {
        return message || "Invalid email or password.";
      }
      case 403: {
        return message || "Insufficient scope to create resource.";
      }
      case 409: {
        return message || "Resource already exists.";
      }
      case 422: {
        return message || "Unprocessable Entity";
      }
      case 429: {
        return message || "Too many requests.";
      }
      case 500: {
        return (
          message ||
          "Something unexpected happened. Our team is checking it out."
        );
      }
      default: {
        return message || "Request failed. Please try again.";
      }
    }
  }
  return "An error occurred. Our team is checking it out.";
};

httpClient.interceptors.request.use(function (config) {
  const accessToken = localStorage.getItem("accessToken");
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  } else {
    config.headers.Authorization = "";
  }
  // Remove Content-Type for FormData so the browser sets multipart/form-data with boundary
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }
  return config;
});

httpClient.interceptors.response.use(
  (fulfilledResponse) => fulfilledResponse,
  async (rejectedResponse) => {
    const error = rejectedResponse as AxiosError;
    
    // Handle 401 errors - try to refresh token
    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem('refreshToken');
      const originalRequest = error.config;
      
      // Only retry if we have a refresh token and this isn't already a retry
      if (refreshToken && originalRequest && !(originalRequest as any)._retry) {
        (originalRequest as any)._retry = true;
        
        try {
          // Import authService dynamically to avoid circular dependency
          const { authService } = await import('../features/auth/services/auth.service');
          const tokens = await authService.refreshToken(refreshToken);
          
          // Save new tokens
          localStorage.setItem('accessToken', tokens.accessToken);
          localStorage.setItem('refreshToken', tokens.refreshToken);
          
          // Update the authorization header and retry the original request
          originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
          return httpClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear tokens and reject
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          const { notifySessionCleared } = await import('../features/auth/sessionSync');
          notifySessionCleared();
          return Promise.reject(handleErrorResponse(error));
        }
      }
    }
    
    //handle error responses
    return Promise.reject(handleErrorResponse(error));
  }
);

export default httpClient;
